/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
import React from 'react'; // eslint-disable-line no-unused-vars
import { Map } from 'immutable';
import moment from 'moment';

import { formatDateTime, FORMAT_DATE_WITH_TIMEZONE } from 'utils';
import { makeHttpRequest } from './actions';
import { makeAsyncActionSet } from './asyncActions';

export const USERS = 'api/reports/users';
export const USER_ACTIVITY = 'api/reports/user/activity';
export const USER_COMPLETIONS = 'api/reports/user/completion';
export const COURSE = 'api/reports/course';

const DEFAULT_PAGE_SIZE = 10;

const filtersToParams = filters => filters.merge(
  filters.filter(moment.isMoment).map(
    date => (date ? formatDateTime(date, '', FORMAT_DATE_WITH_TIMEZONE) : ''),
  ),
);

export const DOWNLOAD_CSV_FILE = makeAsyncActionSet('DOWNLOAD_CSV_FILE');
export function downloadCSVFile(reportName, url, data) {
  return makeHttpRequest(
    DOWNLOAD_CSV_FILE,
    url,
    'POST',
    data,
    { reportName },
    reportName,
  );
}

export const DOWNLOAD_SSV_FILE = makeAsyncActionSet('DOWNLOAD_SSV_FILE');
export function downloadSSVFile(reportName, url, data) {
  return makeHttpRequest(
    DOWNLOAD_SSV_FILE,
    url,
    'POST',
    data,
    { reportName },
    reportName,
  );
}

export const CLEAR_DOWNLOAD_HASH = 'CLEAR_DOWNLOAD_HASH';
export function clearDownloadHash(reportName) {
  return { type: CLEAR_DOWNLOAD_HASH, payload: { reportName } };
}

export const GET_REPORT_DATA = makeAsyncActionSet('GET_REPORT_DATA');

export function getReportData(reportName, url, opts = {}, post) {
  const {
    searchString,
    page,
    pageSize,
    ordering,
    filters,
    withFilterTerms,
    additionalParams,
  } = opts;
  return makeHttpRequest(
    GET_REPORT_DATA,
    url,
    post ? 'POST' : 'GET',
    Object.assign({}, {
      q: searchString,
      page,
      page_size: pageSize,
      ordering,
      with_filters: withFilterTerms,
    }, filtersToParams(filters).toJS(), additionalParams.toJS()),
    {
      reportName,
      q: searchString,
      page,
      filters,
      ordering,
      withFilterTerms,
      pageSize,
      additionalParams,
    },
    reportName,
  );
}


export const GET_REPORTS = makeAsyncActionSet('GET_REPORTS');

export function getReports() {
  return makeHttpRequest(
    GET_REPORTS,
    '/api/reports/',
    'GET',
  );
}

export function loadReport(reportName, opts, post = false) {
  return (dispatch, getState) => {
    const {
      searchString,
      ordering,
      page,
      pageSize,
      filters,
      additionalParams,
    } = getState().reports.get(reportName, Map()).toObject();
    const defaults = {
      filters: filters || Map(),
      searchString: searchString || '',
      page: (page && !(opts && ('searchString' in opts || 'pageSize' in opts))) ? page : 1,
      ordering: ordering || '',
      withFilterTerms: false,
      pageSize: pageSize || DEFAULT_PAGE_SIZE,
      additionalParams: additionalParams || Map(),
    };
    const params = Object.assign(defaults, opts);
    const url = getState().reports.getIn(['config', 'urls', reportName], null) || `/${reportName}/`;
    dispatch(getReportData(reportName, url, params, post));
  };
}

export const SET_REPORT_FILTER_TERM = 'SET_REPORT_FILTER_TERM';
export function setReportFilterTerm(reportName, filterName, term, selected) {
  return { type: SET_REPORT_FILTER_TERM, payload: { reportName, filterName, term, selected } };
}

export const SET_REPORT_FILTER_DATE = 'SET_REPORT_FILTER_DATE';
export function setReportFilterDate(reportName, name, date) {
  return { type: SET_REPORT_FILTER_DATE, payload: { reportName, name, date } };
}

export const SET_REPORT_DATE_FIELD = 'SET_REPORT_DATE_FIELD';
export function setReportDateField(reportName, field) {
  return { type: SET_REPORT_DATE_FIELD, payload: { reportName, field } };
}

export const SET_REPORT_DATE_PERIOD = 'SET_REPORT_DATE_PERIOD';
export function setReportDatePeriod(reportName, period) {
  return { type: SET_REPORT_DATE_PERIOD, payload: { reportName, period } };
}

function dispatchActionLoadReport(action, reportName, loadData) {
  return (dispatch) => {
    dispatch(action);
    if (loadData) {
      dispatch(loadReport(reportName, { page: 1 }));
    }
  };
}

export function setFilterTerm(reportName, filterName, term, selected, loadData = true) {
  return dispatchActionLoadReport(
    setReportFilterTerm(reportName, filterName, term, selected),
    reportName,
    loadData,
  );
}

export function setFilterDate(reportName, name, date, loadData = true) {
  return (dispatch) => {
    dispatch(setReportDatePeriod(reportName, -1));
    dispatch(setReportFilterDate(reportName, name, date));
    if (loadData) {
      dispatch(loadReport(reportName, { page: 1 }));
    }
  };
}

export function setSelectedDateField(reportName, field, loadData = true) {
  return dispatchActionLoadReport(
    setReportDateField(reportName, field),
    reportName,
    loadData,
  );
}

export function setDatePeriod(reportName, period, now, loadData = true) {
  return (dispatch) => {
    dispatch(setReportDatePeriod(reportName, period));
    if (period === -1) {
      return;
    }
    const today = now.startOf('day');
    const numOfDaysAgo = moment(today).subtract(period, 'days');
    dispatch(setReportFilterDate(reportName, 'enddate', today));
    dispatch(setReportFilterDate(reportName, 'startdate', numOfDaysAgo));
    if (loadData) {
      dispatch(loadReport(reportName, { page: 1 }));
    }
  };
}

export const SET_REPORT_PAGE = 'SET_REPORT_PAGE';
export function setReportPage(id, page) {
  return { type: SET_REPORT_PAGE, payload: { id, page } };
}

export const SORT_REPORT_DATA = 'SORT_REPORT_DATA';
export function sortReportData(reportName, id, path, reverse) {
  return { type: SORT_REPORT_DATA, payload: { reportName, id, path, reverse } };
}

export const SET_REPORT_CONFIG = 'SET_REPORT_CONFIG';
export function setReportConfig(config) {
  return { type: SET_REPORT_CONFIG, payload: config };
}

export const SET_REPORT_PAGE_SIZE = 'SET_REPORT_PAGE_SIZE';
export function setReportPageSize(reportName, pageSize) {
  return { type: SET_REPORT_PAGE_SIZE, payload: { reportName, pageSize } };
}
